import ReactPixel from 'react-facebook-pixel';
import {
  CURRENCY_TYPE_LIST,
  META_PIXEL_ID,
  PAYMENT_PROVIDER_LINE_ITEM_TYPES
} from '../config/constants';

const onTrackAnalytics = (title, data) => {
  try {
    ReactPixel.track(title, data);
  } catch (error) {
    console.error('Error in sportsheadzReactPixel', error);
  }
};

const getStoreProductData = (
  ecomStoreCart,
  cartLineItem,
  { cartProduct = null },
  ecomStore = null
) => ({
  id: cartLineItem.uuid,
  uuid: cartLineItem.uuid,
  productUuid: cartLineItem.productUuid,
  urlHandle: cartLineItem.urlHandle,
  quantity: cartLineItem.quantity,
  priceInCents: cartLineItem.subtotalInCents,
  currency: cartLineItem.currency.toUpperCase(),
  fkCurrencyType: cartLineItem.fkCurrencyType,
  name: cartLineItem.productName,
  previewImage: cartLineItem.previewImage,
  productType: cartLineItem.productType,
  storeName: ecomStore?.name,
  fkEcomStore: ecomStoreCart.fkEcomStore,
  fkEcomCategory: cartLineItem.fkEcomCategory,
  isBundleProduct: cartLineItem.isBundleProduct,
  ...(cartProduct
    ? {
        vendorProductUuid:
          cartProduct.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
            .uuid,
        vendorName:
          cartProduct.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
            .vendorName,
        isTaxExempt:
          cartProduct.ecomStoreProductVariant.ecomStoreProduct.isTaxExempt
      }
    : {
        bundleProducts: cartLineItem.bundleItems.map((li) => {
          const bundleItemCartProduct = (
            ecomStoreCart.ecomStoreCartProducts ||
            ecomStoreCart.ecomOrderProducts
          ).find(
            (c) =>
              c.pkEcomStoreCartProduct === li.entityId ||
              c.pkEcomOrderProduct === li.entityId
          );
          return getStoreProductData(
            ecomStoreCart,
            li,
            { cartProduct: bundleItemCartProduct },
            ecomStore
          );
        })
      })
});

const getStoreCartProductTrackingData = (
  ecomStoreCart,
  { ecomStoreCartProducts = [], ecomStoreCartPackages = [] },
  ecomStore = null,
  isAdding = false
) => {
  try {
    const trackedProducts = ecomStoreCartProducts
      .map((p) => {
        const cartProduct = (
          ecomStoreCart.ecomStoreCartProducts || ecomStoreCart.ecomOrderProducts
        ).find(
          (c) =>
            (!!c.pkEcomStoreCartProduct &&
              c.pkEcomStoreCartProduct === p.pkEcomStoreCartProduct) ||
            (!!c.pkEcomOrderProduct &&
              c.pkEcomOrderProduct === p.pkEcomOrderProduct) ||
            (c.ecomStoreProductVariant.ecomVendorProductVariant.uuid ===
              p.uuid &&
              !c.fkEcomStoreCartPackage &&
              !c.fkEcomOrderPackage)
        );

        if (!cartProduct) {
          return null;
        }

        const cartLineItem = (
          ecomStoreCart.checkoutCart || ecomStoreCart.checkoutBalance
        ).lineItems.find(
          (i) =>
            (i.entityId === cartProduct.pkEcomStoreCartProduct ||
              i.entityId === cartProduct.pkEcomOrderProduct) &&
            !i.isBundleProduct &&
            i.lineItemType === PAYMENT_PROVIDER_LINE_ITEM_TYPES.ecommerce.value
        );

        if (!cartLineItem) {
          return null;
        }

        return getStoreProductData(
          ecomStoreCart,
          {
            ...cartLineItem,
            quantity: isAdding ? p.count : cartLineItem.quantity
          },
          { cartProduct },
          ecomStore
        );
      })
      .filter((p) => !!p);

    const trackedPackages = ecomStoreCartPackages
      .map((p) => {
        const cartPackage = (
          ecomStoreCart.ecomStoreCartPackages || ecomStoreCart.ecomOrderPackages
        ).find(
          (c) =>
            (!!c.pkEcomStoreCartPackage &&
              c.pkEcomStoreCartPackage === p.pkEcomStoreCartPackage) ||
            (!!c.pkEcomOrderPackage &&
              c.pkEcomOrderPackage === p.pkEcomOrderPackage) ||
            (c.fkEcomStoreProductPackage === p.fkEcomStoreProductPackage &&
              p.ecomStoreCartProducts.every((cp) =>
                (
                  ecomStoreCart.ecomStoreCartProducts ||
                  ecomStoreCart.ecomOrderProducts
                ).find(
                  (f) =>
                    f.ecomStoreProductVariant.ecomVendorProductVariant.uuid ===
                      cp.uuid &&
                    (f.fkEcomStoreCartPackage === c.pkEcomStoreCartPackage ||
                      f.fkEcomOrderPackage === c.pkEcomOrderPackage)
                )
              ))
        );

        if (!cartPackage) {
          return null;
        }

        const cartLineItem = (
          ecomStoreCart.checkoutCart || ecomStoreCart.checkoutBalance
        ).lineItems.find(
          (i) =>
            (i.entityId === cartPackage.pkEcomStoreCartPackage ||
              i.entityId === cartPackage.pkEcomOrderPackage) &&
            i.isBundleProduct &&
            i.lineItemType === PAYMENT_PROVIDER_LINE_ITEM_TYPES.ecommerce.value
        );

        if (!cartLineItem) {
          return null;
        }

        return getStoreProductData(
          ecomStoreCart,
          {
            ...cartLineItem,
            quantity: isAdding ? p.count : cartLineItem.quantity
          },
          {},
          ecomStore
        );
      })
      .filter((p) => !!p);

    return [...trackedProducts, ...trackedPackages];
  } catch (error) {
    console.error('Error parsing analytics product data', error);
    return null;
  }
};

const translateUserCart = (userCart) => {
  try {
    if (!userCart.pkEcomStoreCart && !userCart.ecomStoreCart) {
      return null;
    }

    const ecomStoreCart = {
      ...userCart,
      ...userCart.ecomStoreCart,
      checkoutCart: userCart.cart || userCart.checkoutCart
    };

    return ecomStoreCart;
  } catch (error) {
    console.error('Error parsing analytics cart data', error);
    return null;
  }
};

const webstoreTrackingHelper = {
  initializeMetaPixel: () => {
    try {
      ReactPixel.init(META_PIXEL_ID, null, {
        autoConfig: true,
        debug: false
      });
    } catch (error) {
      console.error('Failed to initialize Meta Pixel:', error);
    }
  },

  viewStoreProduct: (ecomStoreProduct, ecomStore) => {
    try {
      const productPricing = ecomStoreProduct.ecomStoreProductVariants.reduce(
        (acc, { price }) => {
          if (price < acc.minPrice) acc.minPrice = price;
          if (price > acc.maxPrice) acc.maxPrice = price;
          return acc;
        },
        {
          minPrice: ecomStoreProduct.ecomStoreProductVariants[0].price,
          maxPrice: ecomStoreProduct.ecomStoreProductVariants[0].price
        }
      );

      onTrackAnalytics('ViewContent', {
        content_ids: [ecomStoreProduct.uuid],
        content_type: 'product',
        value: productPricing.minPrice / 100,
        currency:
          CURRENCY_TYPE_LIST.find(
            (c) =>
              c.value.toString() === ecomStoreProduct.fkCurrencyType.toString()
          )?.abbr || 'CAD',
        contents: [
          {
            name: ecomStoreProduct.ecomVendorProduct.name,
            vendorName: ecomStoreProduct.ecomVendorProduct.vendorName,
            storeName: ecomStore?.name,
            priceInCents: productPricing.minPrice,
            minPriceInCents: productPricing.minPrice,
            maxPriceInCents: productPricing.maxPrice,
            variantCount: ecomStoreProduct.ecomStoreProductVariants.length,
            inputCount:
              ecomStoreProduct.ecomVendorProduct.ecomVendorProductInputs.length,
            mediaCount:
              ecomStoreProduct.ecomVendorProduct.ecomVendorProductMedia.length,
            urlHandle: ecomStoreProduct.urlHandle,
            uuid: ecomStoreProduct.uuid,
            vendorUuid: ecomStoreProduct.ecomVendorProduct.uuid,
            fkEcomVendor: ecomStoreProduct.fkEcomVendor,
            fkEcomCategory: ecomStoreProduct.fkEcomCategory,
            fkEcomStore: ecomStoreProduct.fkEcomStore,
            fkEcomProductStatus: ecomStoreProduct.fkEcomProductStatus,
            fkCurrencyType: ecomStoreProduct.fkCurrencyType,
            isTaxExempt: ecomStoreProduct.isTaxExempt,
            productType: ecomStoreProduct.productType
          }
        ]
      });
    } catch (error) {
      console.error('Error tracking view store product', error);
    }
  },

  searchProducts: (searchQuery) => {
    if (searchQuery) {
      onTrackAnalytics('Search', {
        search_string: searchQuery,
        content_type: 'product'
      });
    }
  },

  addStoreProductToCart: (
    userCart,
    { ecomStoreCartProducts = [], ecomStoreCartPackages = [] },
    ecomStore = null
  ) => {
    try {
      const ecomStoreCart = translateUserCart(userCart);
      if (!ecomStoreCart) {
        return;
      }

      const trackingData = getStoreCartProductTrackingData(
        ecomStoreCart,
        { ecomStoreCartProducts, ecomStoreCartPackages },
        ecomStore,
        true
      );
      if (!trackingData) {
        return;
      }

      trackingData.forEach((data) => {
        onTrackAnalytics('AddToCart', {
          content_ids: [data.uuid],
          content_type: 'product',
          value: (data.priceInCents * data.quantity) / 100,
          currency: data.currency || 'CAD',
          contents: [data]
        });
      });
    } catch (error) {
      console.error('Error tracking add store product to cart', error);
    }
  },

  customizeStoreCartProducts: (
    userCart,
    { ecomStoreCartProducts = [], ecomStoreCartPackages = [] },
    ecomStore = null
  ) => {
    try {
      const ecomStoreCart = translateUserCart(userCart);
      if (!ecomStoreCart) {
        return;
      }

      const trackingData = getStoreCartProductTrackingData(
        ecomStoreCart,
        { ecomStoreCartProducts, ecomStoreCartPackages },
        ecomStore
      );
      if (!trackingData) {
        return;
      }

      trackingData.forEach((data) => {
        onTrackAnalytics('CustomizeProduct', {
          content_ids: [data.uuid],
          content_type: 'product',
          value: (data.priceInCents * data.quantity) / 100,
          currency: data.currency || 'CAD',
          contents: [data]
        });
      });
    } catch (error) {
      console.error('Error tracking customize store cart products', error);
    }
  },

  initiateCheckout: (
    userCart,
    checkoutData,
    ecomStore = null,
    includedProductPriceOnly = false
  ) => {
    try {
      const ecomStoreCart = translateUserCart(userCart);
      if (!ecomStoreCart) {
        return;
      }

      const trackingData = getStoreCartProductTrackingData(
        ecomStoreCart,
        {
          ecomStoreCartProducts: ecomStoreCart.ecomStoreCartProducts.filter(
            (p) => !p.fkEcomStoreCartPackage
          ),
          ecomStoreCartPackages: ecomStoreCart.ecomStoreCartPackages
        },
        ecomStore
      );
      if (!trackingData) {
        return;
      }

      const totalPrice = includedProductPriceOnly
        ? trackingData.reduce(
            (acc, data) => acc + data.priceInCents * data.quantity,
            0
          )
        : ecomStoreCart.checkoutCart.totalInCents;

      onTrackAnalytics('InitiateCheckout', {
        content_ids: [...new Set(trackingData.map((data) => data.uuid))],
        content_type: 'product',
        value: totalPrice / 100,
        currency: ecomStoreCart.checkoutCart.currency.toUpperCase(),
        num_items: trackingData.reduce((acc, data) => acc + data.quantity, 0),
        contents: trackingData.map((p) => ({
          ...p,
          fkEcomStoreCart: ecomStoreCart.pkEcomStoreCart,
          secureId: checkoutData.secureId
        }))
      });
    } catch (error) {
      console.error('Error tracking initiating checkout', error);
    }
  },

  completePurchase: (
    userOrder,
    checkoutSession,
    includedProductPriceOnly = false
  ) => {
    try {
      if (userOrder.pkRegCart && !userOrder.ecomOrder) {
        return;
      }
      const ecomOrder = userOrder.pkRegCart ? userOrder.ecomOrder : userOrder;

      const trackingData = getStoreCartProductTrackingData(
        ecomOrder,
        {
          ecomStoreCartProducts: ecomOrder.ecomOrderProducts.filter(
            (p) => !p.fkEcomOrderPackage
          ),
          ecomStoreCartPackages: ecomOrder.ecomOrderPackages
        },
        ecomOrder.ecomStore
      );
      if (!trackingData) {
        return;
      }

      const totalPrice = includedProductPriceOnly
        ? trackingData.reduce(
            (acc, data) => acc + data.priceInCents * data.quantity,
            0
          )
        : ecomOrder.checkoutBalance.totalInCents;

      onTrackAnalytics('Purchase', {
        content_ids: [...new Set(trackingData.map((data) => data.uuid))],
        content_type: 'product',
        value: totalPrice / 100,
        currency: ecomOrder.checkoutBalance.currency.toUpperCase(),
        num_items: trackingData.reduce((acc, data) => acc + data.quantity, 0),
        contents: trackingData.map((p) => ({
          ...p,
          fkEcomOrder: ecomOrder.pkEcomOrder,
          fkEcomStoreCart: ecomOrder.fkEcomStoreCart,
          secureId: checkoutSession.secureId
        }))
      });
    } catch (error) {
      console.error('Error tracking completed purchase', error);
    }
  }
};

export { webstoreTrackingHelper };
